import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { CalendarDays, Clapperboard, Tv, MonitorPlay, Repeat } from "lucide-react";
import { InnerNav } from "../components/InnerNav";
import { Overline } from "../components/motion";
import { useTranslate } from "../context/TranslateContext";
import { RELEASE_CALENDAR } from "../mock/tv";

const TYPE_META = {
  Theatrical: { c: "#E8873A", icon: Clapperboard },
  "TV Premiere": { c: "#C0392B", icon: Tv },
  "New Episode": { c: "#3B6EA5", icon: Tv },
  "OTT Release": { c: "#8E44AD", icon: MonitorPlay },
  Repeat: { c: "#B5AFA6", icon: Repeat },
};
const TYPES = ["All", ...Object.keys(TYPE_META)];
const LANGS = ["All", "Hindi", "Malayalam", "Tamil", "Telugu", "Rajasthani", "Kannada"];

export default function ReleaseCalendar() {
  const { t } = useTranslate();
  const navigate = useNavigate();
  const [type, setType] = useState("All");
  const [lang, setLang] = useState("All");
  useEffect(() => { window.scrollTo(0, 0); }, []);

  const rows = useMemo(() => RELEASE_CALENDAR
    .filter((r) => (type === "All" || r.type === type) && (lang === "All" || r.language === lang))
    .sort((a, b) => a.date.localeCompare(b.date)), [type, lang]);

  const go = (r) => navigate(r.ref.kind === "movie" ? `/movie/${r.ref.id}` : `/series/${r.ref.slug}`);
  const fmt = (d) => new Date(d).toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short" });

  return (
    <div className="min-h-screen bg-gradient-to-b from-paper to-surface">
      <InnerNav />
      <div className="mx-auto max-w-[1100px] px-6 py-14 md:px-12 md:py-16">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <Overline>Release Calendar</Overline>
            <h1 className="mt-3 font-display text-5xl font-semibold tracking-tight text-cream md:text-7xl">{t("What's ")}<span className="italic text-gradient-gold">{t("coming")}</span></h1>
            <p className="mt-3 font-body text-ash">{t("Theatrical, television and streaming releases — clearly distinguished. Some dates are tentative.")}</p>
          </div>
          
        </div>

        <div className="mt-8 flex flex-wrap gap-2" data-testid="calendar-filters">
          {TYPES.map((x) => (
            <button key={x} onClick={() => setType(x)} data-testid={`cal-type-${x}`}
              className={`rounded-full border px-4 py-2 font-body text-sm transition-colors ${type === x ? "border-gold bg-gold text-ink" : "border-edge text-cream hover:border-gold"}`}>{t(x)}</button>
          ))}
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          {LANGS.map((x) => (
            <button key={x} onClick={() => setLang(x)} className={`rounded-full px-3 py-1.5 font-body text-sm ${lang === x ? "text-gold" : "text-ash hover:text-cream"}`}>{t(x)}</button>
          ))}
        </div>

        <div className="mt-8 space-y-3" data-testid="calendar-list">
          {rows.map((r, i) => { const meta = TYPE_META[r.type]; const Icon = meta.icon; return (
            <motion.button key={i} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.04 }}
              onClick={() => go(r)} data-testid={`cal-row-${i}`}
              className="flex w-full items-center gap-4 rounded-2xl border border-edge bg-surface/40 px-5 py-4 text-left transition-colors hover:border-gold">
              <div className="w-24 shrink-0 font-body text-sm text-ash">{fmt(r.date)}</div>
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full" style={{ background: meta.c + "22", color: meta.c }}><Icon className="h-5 w-5" /></span>
              <div className="min-w-0 flex-1">
                <div className="truncate font-body text-cream">{r.title}</div>
                <div className="font-body text-xs text-ash">{r.language} · {r.provider}</div>
              </div>
              <span className="rounded-full px-2.5 py-1 font-body text-xs" style={{ background: meta.c + "22", color: meta.c }}>{t(r.type)}</span>
              {r.tentative && <span className="rounded-full border border-saffron/40 bg-saffron/10 px-2 py-0.5 font-body text-[0.65rem] text-saffron" data-testid={`cal-tentative-${i}`}>{t("Tentative")}</span>}
            </motion.button>
          ); })}
          {rows.length === 0 && <p className="py-16 text-center font-body text-ash">{t("No releases match these filters.")}</p>}
        </div>
      </div>
    </div>
  );
}
